/*
 * GlobalTimeTracker — the ONE unified time-tracking control in the top app-bar.
 *
 * Consolidates what used to be two separate UIs (the inline "Now tracking …"
 * overlay banner + a corner dropdown) into a single pill + dropdown:
 *   - Pill: a recording dot + live MM:SS + the project, with a stop button.
 *     Idle → a play button + "Start timer".
 *   - Dropdown: a dark "Now tracking" strip with a LIVE task switcher (change
 *     task without stopping the clock) + Stop, then a two-pane picker —
 *     Favorites / All projects (searchable, star to pin) on the left, the
 *     selected project's tasks to start on the right.
 *
 * The Favorites + project list + per-project tasks are the SAME ones the Time
 * Tracker tool uses (TimeTrackingContext: projects, favorites,
 * getTasksForProject), so a project starred in the tracker — client OR internal
 * — shows here with its own unique tasks. One favorites store, one task source.
 *
 * Toolbar-native (not a floating/portal element) so it scrolls with the static
 * app-bar. The browser-tab recording indicator (title + paprika favicon) lives
 * in TimeTrackingContext.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Popover, Typography, IconButton, Tooltip, InputBase, keyframes,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../../contexts/AuthContext';
import { useTimeTracking, useElapsedSeconds } from '../../contexts/TimeTrackingContext';
import type { TimeProject } from '../../services/api';

const PAPRIKA = 'var(--paprika)';

function fmtFull(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
function fmtShort(secs: number): string {
  return fmtFull(secs).slice(3); // MM:SS
}

function taskColor(label: string): string {
  const l = (label || '').toLowerCase();
  if (l.includes('on call')) return '#0443EF';
  if (l.includes('implement')) return '#2E7D32';
  if (l.includes('support')) return '#F76100';
  if (l.includes('document')) return '#7A3FB5';
  if (l.includes('wrap')) return '#0E9AA0';
  if (l.includes('rock')) return '#C2410C';
  if (l.includes('sales')) return '#0443EF';
  if (l.includes('account')) return '#0E7490';
  if (l.includes('hr')) return '#B45309';
  if (l.includes('event')) return '#9333EA';
  if (l.includes('misc')) return '#64748B';
  return '#9AA0A8';
}

const breathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(.7); opacity: .55; }
`;

const RecordDot: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = PAPRIKA }) => (
  <Box sx={{ position: 'relative', width: size, height: size, flex: 'none', display: 'inline-block' }}>
    <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: color, animation: `${breathe} 2.2s ease-in-out infinite` }} />
  </Box>
);

const DiamondDot: React.FC<{ color: string }> = ({ color }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: '3px', transform: 'rotate(45deg)', bgcolor: color, flex: 'none' }} />
);

const GlobalTimeTracker: React.FC = () => {
  const { user } = useAuth();
  const {
    activeTimer, projects, favorites, toggleFavorite,
    getTasksForProject, startTimer, stopTimer, switchTask,
    refreshProjects, refreshProjectTaskMap,
  } = useTimeTracking();
  const elapsedSeconds = useElapsedSeconds();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [seg, setSeg] = useState<'Favorites' | 'All'>('Favorites');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  const open = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    // Projects + favorites load on mount; refresh in the background on open so
    // a newly-starred project / new task shows without a reload.
    refreshProjects();
    refreshProjectTaskMap();
  }, [refreshProjects, refreshProjectTaskMap]);
  const close = useCallback(() => setAnchorEl(null), []);

  const start = useCallback(async (projectId: number, taskId: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await startTimer(projectId, taskId); // optimistic in the context
      close();
    } finally { setBusy(false); }
  }, [busy, startTimer, close]);

  const onSwitchTask = useCallback((taskId: number) => {
    if (activeTimer) switchTask(activeTimer.project_id, taskId);
  }, [activeTimer, switchTask]);

  const matches = (p: TimeProject) =>
    (p.name || '').toLowerCase().includes(q.toLowerCase());
  const favList = useMemo(() => projects.filter((p) => favorites.has(p.id) && matches(p)), [projects, favorites, q]);
  const restList = useMemo(() => projects.filter((p) => !favorites.has(p.id) && matches(p)), [projects, favorites, q]);

  // Default the right pane to the running project, else first favorite, else first.
  const effectiveSelected = selected
    ?? activeTimer?.project_id
    ?? projects.find((p) => favorites.has(p.id))?.id
    ?? projects[0]?.id
    ?? null;
  const selectedProject = projects.find((p) => p.id === effectiveSelected) || null;
  const selectedTasks = selectedProject ? getTasksForProject(selectedProject.id) : [];
  const currentTasks = activeTimer ? getTasksForProject(activeTimer.project_id) : [];

  if (!user) return null;
  const running = !!activeTimer;
  const KICKER = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase' as const };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, gap: 0.5 }}>
      {/* ── Pill ── */}
      <Box
        onClick={anchorEl ? close : open}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none',
          height: 32, pl: 1, pr: 1.25, borderRadius: '6px', fontSize: 12.5, fontWeight: 700,
          fontFamily: '"Inter Tight", sans-serif',
          color: 'text.primary', bgcolor: running ? 'rgba(247,97,0,0.08)' : 'background.paper',
          border: '1.5px solid', borderColor: running ? PAPRIKA : 'divider',
          '&:hover': { bgcolor: running ? 'rgba(247,97,0,0.12)' : 'action.hover' },
        }}
      >
        {running
          ? <RecordDot size={11} />
          : <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid', borderColor: 'text.disabled', flex: 'none' }} />}
        <span style={{ fontVariantNumeric: 'tabular-nums', color: running ? PAPRIKA : undefined }}>
          {running ? fmtShort(elapsedSeconds) : '00:00'}
        </span>
        <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
        <Box component="span" sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: running ? 'text.primary' : 'text.secondary' }}>
          {running ? activeTimer!.project_name : 'Start timer'}
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </Box>
      {running && (
        <Tooltip title="Stop timer">
          <IconButton size="small" onClick={() => stopTimer()} aria-label="Stop timer"
            sx={{ width: 32, height: 32, bgcolor: PAPRIKA, '&:hover': { bgcolor: '#d95400' } }}>
            <Box sx={{ width: 10, height: 10, bgcolor: '#fff', borderRadius: '3px' }} />
          </IconButton>
        </Tooltip>
      )}

      {/* ── Dropdown ── */}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, width: 560, maxWidth: '94vw', borderRadius: '6px', overflow: 'hidden' } } }}
      >
        <Box sx={{ opacity: busy ? 0.6 : 1, pointerEvents: busy ? 'none' : 'auto' }}>
          {/* now-tracking strip */}
          {running ? (
            <Box sx={{ bgcolor: '#16181B', color: '#F3F3F3', px: 2.25, py: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <RecordDot size={13} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ ...KICKER, color: 'rgba(243,243,243,0.46)' }}>Now tracking</Typography>
                  <Typography sx={{ fontFamily: '"Inter Tight", sans-serif', fontWeight: 700, fontSize: 14, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {activeTimer!.project_name}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: '"Inter Tight", sans-serif', fontWeight: 700, fontSize: 22, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}>
                  {fmtFull(elapsedSeconds)}
                </Typography>
                <Box component="button" onClick={() => stopTimer()} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.75, py: 0.9, bgcolor: PAPRIKA, color: '#fff',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: '"Inter Tight", sans-serif', fontWeight: 700, fontSize: 12.5,
                }}>
                  <Box sx={{ width: 9, height: 9, bgcolor: '#fff', borderRadius: '3px' }} /> Stop
                </Box>
              </Box>
              {currentTasks.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ ...KICKER, color: 'rgba(243,243,243,0.46)', mr: 0.25 }}>Switch task</Typography>
                  {currentTasks.map((t) => {
                    const on = t.id === activeTimer!.task_id;
                    const color = taskColor(t.name);
                    return (
                      <Box key={t.id} component="button" onClick={() => !on && onSwitchTask(t.id)} sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.65, borderRadius: '999px',
                        cursor: on ? 'default' : 'pointer', fontFamily: '"Inter Tight", sans-serif', fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap',
                        border: '1px solid', borderColor: on ? PAPRIKA : 'rgba(255,255,255,0.18)',
                        bgcolor: on ? PAPRIKA : 'rgba(255,255,255,0.05)', color: on ? '#fff' : 'rgba(243,243,243,0.78)',
                      }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '3px', transform: 'rotate(45deg)', bgcolor: on ? '#fff' : color }} />
                        {t.name}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ bgcolor: '#16181B', color: 'rgba(243,243,243,0.7)', px: 2.25, py: 1.75, fontFamily: '"Inter Tight", sans-serif', fontWeight: 600, fontSize: 13.5 }}>
              Timer idle — pick a project, then a task to start.
            </Box>
          )}

          {/* two-pane picker */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            {/* projects */}
            <Box sx={{ borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', maxHeight: 360 }}>
              <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.4, bgcolor: 'action.hover', borderRadius: '6px' }}>
                  {(['Favorites', 'All'] as const).map((s) => (
                    <Box key={s} component="button" onClick={() => setSeg(s)} sx={{
                      flex: 1, py: 0.6, borderRadius: '4px', border: 'none', cursor: 'pointer',
                      fontFamily: '"Inter Tight", sans-serif', fontWeight: 700, fontSize: 12.5,
                      bgcolor: seg === s ? 'background.paper' : 'transparent', color: seg === s ? 'text.primary' : 'text.secondary',
                      boxShadow: seg === s ? '0 1px 2px rgba(16,18,21,0.12)' : 'none',
                    }}>{s === 'Favorites' ? '★ Favorites' : 'All projects'}</Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, height: 32, border: '1px solid', borderColor: 'divider', borderRadius: '6px' }}>
                  <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                  <InputBase value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" sx={{ fontSize: 12.5, flex: 1 }} />
                </Box>
              </Box>
              <Box sx={{ overflowY: 'auto', px: 1, pb: 1.25, flex: 1 }}>
                {projects.length === 0 ? (
                  <Typography sx={{ px: 1, py: 2, fontSize: 12.5, color: 'text.secondary' }}>No projects yet.</Typography>
                ) : seg === 'Favorites' ? (
                  favList.length ? favList.map((p) => <ProjectRow key={p.id} p={p} selected={effectiveSelected === p.id} live={running && activeTimer!.project_id === p.id} fav onSelect={() => setSelected(p.id)} onFav={() => toggleFavorite(p.id)} />)
                    : <Typography sx={{ px: 1, py: 2, fontSize: 12.5, color: 'text.secondary' }}>No favorites yet — star a project (here or in the Time Tracker) to pin it.</Typography>
                ) : (
                  <>
                    {favList.length > 0 && <Typography sx={{ ...KICKER, px: 1, pt: 1.25, pb: 0.75, color: PAPRIKA }}>★ Favorites</Typography>}
                    {favList.map((p) => <ProjectRow key={p.id} p={p} selected={effectiveSelected === p.id} live={running && activeTimer!.project_id === p.id} fav onSelect={() => setSelected(p.id)} onFav={() => toggleFavorite(p.id)} />)}
                    {restList.length > 0 && <Typography sx={{ ...KICKER, px: 1, pt: 1.25, pb: 0.75, color: 'text.secondary' }}>All projects</Typography>}
                    {restList.map((p) => <ProjectRow key={p.id} p={p} selected={effectiveSelected === p.id} live={running && activeTimer!.project_id === p.id} fav={false} onSelect={() => setSelected(p.id)} onFav={() => toggleFavorite(p.id)} />)}
                  </>
                )}
              </Box>
            </Box>

            {/* start tracking */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', maxHeight: 360, overflowY: 'auto' }}>
              <Typography sx={{ ...KICKER, color: 'text.secondary' }}>Start tracking</Typography>
              {selectedProject ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                    <DiamondDot color={selectedProject.is_internal ? '#9AA0A8' : '#0443EF'} />
                    <Typography sx={{ fontFamily: '"Inter Tight", sans-serif', fontWeight: 700, fontSize: 14 }}>{selectedProject.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedTasks.length === 0 && (
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No tasks set up for this project.</Typography>
                    )}
                    {selectedTasks.map((t) => {
                      const liveHere = running && activeTimer!.project_id === selectedProject.id && activeTimer!.task_id === t.id;
                      return (
                        <Box key={t.id} component="button"
                          onClick={() => liveHere ? stopTimer() : start(selectedProject.id, t.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1, borderRadius: '6px', cursor: 'pointer', textAlign: 'left',
                            border: '1px solid', borderColor: liveHere ? PAPRIKA : 'divider',
                            bgcolor: liveHere ? 'rgba(247,97,0,0.08)' : 'background.paper',
                            '&:hover': { bgcolor: liveHere ? 'rgba(247,97,0,0.12)' : 'action.hover' },
                          }}>
                          <DiamondDot color={taskColor(t.name)} />
                          <Typography sx={{ flex: 1, fontFamily: '"Inter Tight", sans-serif', fontWeight: 600, fontSize: 13.5 }}>{t.name}</Typography>
                          {liveHere
                            ? <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontFamily: '"Inter Tight", sans-serif', fontWeight: 700, fontSize: 11, color: PAPRIKA }}><RecordDot size={8} /> Live</Box>
                            : <PlayArrowIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>Pick a project on the left.</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

const ProjectRow: React.FC<{
  p: TimeProject; selected: boolean; live: boolean; fav: boolean; onSelect: () => void; onFav: () => void;
}> = ({ p, selected, live, fav, onSelect, onFav }) => (
  <Box onClick={onSelect} sx={{
    display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 1.25, py: 1, borderRadius: '4px', cursor: 'pointer',
    bgcolor: selected ? 'rgba(4,67,239,0.08)' : 'transparent', '&:hover': { bgcolor: selected ? 'rgba(4,67,239,0.08)' : 'action.hover' },
  }}>
    <Box component="span" onClick={(e) => { e.stopPropagation(); onFav(); }} title={fav ? 'Remove favorite' : 'Add favorite'} sx={{ display: 'inline-flex', flex: 'none' }}>
      {fav ? <StarIcon sx={{ fontSize: 16, color: PAPRIKA }} /> : <StarBorderIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
    </Box>
    <Typography sx={{ flex: 1, minWidth: 0, fontFamily: '"Inter Tight", sans-serif', fontWeight: 600, fontSize: 13.5, color: selected ? '#0443EF' : 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
    {live && <RecordDot size={9} />}
  </Box>
);

export default GlobalTimeTracker;
